import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, BookOpen } from 'lucide-react';

const HelpPage = ({ onOpenAppMenu }) => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 font-['HelveticaRegular']">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center gap-4">
              <button
                onClick={onOpenAppMenu}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                aria-label="Open menu"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
              <div className="flex items-center gap-2">
                <BookOpen className="text-blue-600" size={24} />
                <h1 className="text-xl font-bold text-gray-900 font-['bpg-caps']">
                  აფხაზური ენის კურსი
                </h1>
              </div>
            </div>
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
            >
              <ArrowLeft size={16} />
              <span className="font-['bpg-caps']">მთავარი</span>
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-xl shadow-lg p-8">
          {/* Title */}
          <h1 className="text-3xl font-bold text-gray-900 mb-8 text-center font-['bpg-caps']">
            დახმარება
          </h1>

          {/* Registration Section */}
          <section className="mb-8">
            <p className="text-gray-700 leading-relaxed mb-4">
              სწავლის დასაწყებად არაა აუცილებელი რეგისტრაციის გავლა, თუმცა დარეგისტრირებულ მომხმარებელს აქვს შესაძლებლობა, თვალი ადევნოს საკუთარ პროგრესს აფხაზური ენის სწავლისას, კურსის თავიდან გავლის სურვილის შემთხვევაში — გაანულოს პროგრესი.
            </p>
          </section>

          {/* Course Structure */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 font-['bpg-caps']">კურსის სტრუქტურა</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              აფხაზური ენის ელექტრონული კურსი შედგება <strong>30 გაკვეთილისა</strong> და <strong>სამი საკონტროლო ქვიზისგან</strong>.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              საიტი აღჭურვილია <strong>ვირტუალური კლავიატურით</strong>, რომლის მეშვეობითაც შესაძლებელია წერითი დავალებების შესრულება.
            </p>
          </section>

          {/* Lesson Content */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 font-['bpg-caps']">გაკვეთილის შემადგენლობა</h2>
            <p className="text-gray-700 mb-4">თითოეული გაკვეთილი მოიცავს:</p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4 mb-4">
              <li>თეორიულ საკითხებს</li>
              <li>თემატურ ლექსიკას</li>
              <li>ტექსტებს/დიალოგებს</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mb-4">
              ყოველ გაკვეთილში წარმოდგენილია გაკვეთილის თემებზე მომზადებული სავარჯიშოები. სავარჯიშოების არასწორად შესრულებისას გამოჩნდება მითითება — პასუხის სწორი ვარიანტი.
            </p>
            <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded-r-lg">
              <p className="text-gray-700 flex items-center gap-2">
                <span className="text-2xl">▶</span>
                <span>ნიშნის მქონე ყველა ტექსტი გახმოვანებულია.</span>
              </p>
            </div>
          </section>

          {/* Dictionary Section */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 font-['bpg-caps']">ლექსიკონი</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              კურსს ერთვის მასში შემავალი სიტყვების ლექსიკონი, რომელიც ხელმისაწვდომია სწავლის ყველა ეტაპზე.
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>სიტყვები განლაგებულია აფხაზური ანბანის მიხედვით.</li>
              <li>მახვილი აღნიშნულია შესაბამის მარცვლებზე როგორც ლექსიკურ მასალაში, ისე ყველა აფხაზურ ტექსტში.</li>
              <li>ყველა აფხაზურ სიტყვას თან ახლავს მითითება მეტყველების ნაწილზე. ადამიანის კლასის რიცხვით სახელებს მიწერილი აქვს (ად.), ნივთისას აღნიშვნა არ აქვს.</li>
            </ul>
          </section>

          {/* Lexical Forms Table */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 font-['bpg-caps']">ლექსიკური ერთეულების ფორმები</h2>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse border border-gray-300">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border border-gray-300 p-3 text-left font-semibold">მეტყველების ნაწილი</th>
                    <th className="border border-gray-300 p-3 text-left font-semibold">აღნიშვნა</th>
                    <th className="border border-gray-300 p-3 text-left font-semibold">თანმხლები ფორმა/ინფორმაცია</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-gray-300 p-3 font-semibold">არსებითი სახელი</td>
                    <td className="border border-gray-300 p-3">(არს.)</td>
                    <td className="border border-gray-300 p-3">მრავლობითი რიცხვის სუფიქსები: გონიერთა კლასის (-цәа) და არაგონიერთა კლასის (-қәа).</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="border border-gray-300 p-3 font-semibold">ზედსართავი სახელი</td>
                    <td className="border border-gray-300 p-3">(ზედს.)</td>
                    <td className="border border-gray-300 p-3">ახლავს ინფინიტური ფორმა.</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 p-3 font-semibold">ზმნა</td>
                    <td className="border border-gray-300 p-3">(გრდუვ.) / (გრდმ.)</td>
                    <td className="border border-gray-300 p-3">მოცემულია მასდარის ფორმით. თან ახლავს ად. ან მ.კ. პირ-კლასის მქონე ფორმა (აწმყო) და მითითებულია, ზმნის ეს ფორმა გარდამავალია (გრდმ.) თუ გარდაუვალი (გრდუვ.).</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* Abbreviations */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4 font-['bpg-caps']">კურსში გამოყენებული შემოკლებები</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-gray-700 text-sm">
              <div className="space-y-1">
                <p><strong>ად.</strong> - ადამიანი</p>
                <p><strong>ად. კ.</strong> - ადამიანის კლასი</p>
                <p><strong>არაგონ.</strong> - არაგონიერი</p>
                <p><strong>არს.</strong> - არსებითი სახელი</p>
                <p><strong>გონ.</strong> - გონიერი</p>
                <p><strong>გრდმ.</strong> - გარდამავალი ზმნა</p>
                <p><strong>გრდუვ.</strong> - გარდაუვალი ზმნა</p>
                <p><strong>ზედს.</strong> - ზედსართავი სახელი</p>
                <p><strong>ზმნზდ.</strong> - ზმნიზედა</p>
                <p><strong>თანდ.</strong> - თანდებული</p>
                <p><strong>ინვ.</strong> - ინვერსიული</p>
              </div>
              <div className="space-y-1">
                <p><strong>კ.</strong> - კაცი</p>
                <p><strong>ლაბ.</strong> - ლაბილური ზმნა</p>
                <p><strong>მრ. რ.</strong> - მრავლობითი რიცხვი</p>
                <p><strong>მხ. რ.</strong> - მხოლოობითი რიცხვი</p>
                <p><strong>ნ.</strong> - ნივთი</p>
                <p><strong>ნაცვ.</strong> - ნაცვალსახელი</p>
                <p><strong>ნაწ.</strong> - ნაწილაკი</p>
                <p><strong>რიცხვ.</strong> - რიცხვითი სახელი</p>
                <p><strong>სტატიკ.</strong> - სტატიკური</p>
                <p><strong>ქ.</strong> - ქალი</p>
              </div>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
};

export default HelpPage;
